import { z } from 'zod';

/**
 * Schéma d'un niveau (concept §7.1) — la frontière de confiance du contenu.
 *
 * Un niveau est un fichier JSON autonome : il est validé ici avant TOUTE
 * utilisation (fichier du repo, réponse d'API, `submit_level` du MCP en
 * phase 4, hook `window.__md.loadLevel` des tests).
 *
 * Ce schéma ne valide que la FORME. Les règles structurelles (unicité des
 * spawns, tuiles connues du module, dimensions cohérentes) sont vérifiées par
 * le parser du moteur, qui connaît la légende du GameModule.
 */

/**
 * Borne haute d'une histoire de niveau : une à deux phrases, pas un synopsis.
 * Assez pour tenir sous la liste des niveaux sans la repousser hors de l'écran.
 */
export const LEVEL_DESCRIPTION_MAX_LENGTH = 280;

export const LevelMetaSchema = z.object({
  /** `{game}.{world}.{chapitre}.{niveau}`. */
  id: z.string().min(1),
  game: z.string().min(1),
  world: z.string().min(1),
  chapter: z.number().int().positive(),
  level: z.number().int().positive(),
  name: z.string().min(1),
  /**
   * L'histoire du niveau — une ou deux phrases qui font avancer l'évasion,
   * affichées à la sélection. **Optionnelle** : les niveaux soumis via le MCP
   * et les bancs d'essai n'en portent pas, et un niveau muet reste valide.
   */
  description: z.string().min(1).max(LEVEL_DESCRIPTION_MAX_LENGTH).optional(),
  parTimeSeconds: z.number().positive(),
  /** Mécaniques employées — ⊆ liste blanche du chapitre (vérifié en phase 4). */
  mechanics: z.array(z.string()).default([]),
  author: z.string().min(1),
});

export const LevelGridSchema = z.object({
  /** Largeur en tuiles. */
  width: z.number().int().positive(),
  /** Hauteur en tuiles. */
  height: z.number().int().positive(),
  /** Taille d'une tuile en pixels — indication de rendu uniquement. */
  tileSize: z.number().int().positive(),
});

/**
 * Entité posée sur la grille. Le `type` est résolu par les `entityFactories`
 * du GameModule ; les champs additionnels sont propres à chaque type et
 * validés par la factory correspondante (phase 2).
 */
export const EntityDefSchema = z
  .object({
    type: z.string().min(1),
    x: z.number(),
    y: z.number(),
  })
  .catchall(z.unknown());

export const LevelSchema = z.object({
  meta: LevelMetaSchema,
  grid: LevelGridSchema,
  /** Carte ASCII : une chaîne par ligne, 1 caractère = 1 tuile. */
  tiles: z.array(z.string()).min(1),
  entities: z.array(EntityDefSchema).default([]),
});

export type LevelMeta = z.infer<typeof LevelMetaSchema>;
export type LevelGrid = z.infer<typeof LevelGridSchema>;
export type EntityDef = z.infer<typeof EntityDefSchema>;
export type Level = z.infer<typeof LevelSchema>;

/** Fichier `world.json` d'un world (spec §2). */
export const WorldFileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  theme: z.string().min(1),
  description: z.string().default(''),
});

/** Un chapitre = un palier de difficulté + sa liste blanche de mécaniques. */
export const ChapterFileEntrySchema = z.object({
  chapter: z.number().int().positive(),
  name: z.string().min(1),
  difficulty: z.number().int().min(1).max(5),
  mechanics: z.array(z.string()).default([]),
  /**
   * Passages coopératifs exigés par le chapitre (concept §7.4 règle 5) :
   * nombre minimal de transitions du plan de résolution infranchissables en
   * solo. Croissant avec la difficulté, déclaré dans `chapters.json`.
   */
  minCoopPassages: z.number().int().nonnegative().default(1),
});

/** Fichier `chapters.json` d'un world (spec §2). */
export const ChaptersFileSchema = z.object({
  world: z.string().min(1),
  chapters: z.array(ChapterFileEntrySchema).min(1),
});

export type WorldFile = z.infer<typeof WorldFileSchema>;
export type ChapterFileEntry = z.infer<typeof ChapterFileEntrySchema>;
export type ChaptersFile = z.infer<typeof ChaptersFileSchema>;
